import { IconsConstants } from './../../../shared/common/icons-constants';
import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';
import { ApplicationType, LanguageEnum } from 'src/app/core/common/enum';
import { AuthUser } from 'src/app/core/data/model/auth-user-model';
import {
  AppBloc,
  AppBlocState,
  AuthenticatedUserDataReadyState,
  UpdateAplicationTheme,
} from 'src/app/shared/bloc/app-bloc';
import { InitEvent } from 'src/app/shared/bloc/base-bloc';
import { ProfileMenuAction, RoutesConstants, SectionStateStatus } from 'src/app/shared/common';
import { BaseComponent } from 'src/app/shared/component/base-component/base.component';
import { HeaderLogOutEvent } from '../bloc/header-bloc/header-events';
import { 
  HeaderBloc, 
  HeaderLoggedOutState, 
  HeaderState, 
} from '../bloc/header-bloc';
import { IconSvgComponent } from 'src/app/shared/component/icon/svg-icon.component';
import { FormControl, FormGroup } from '@angular/forms';
import { NavBarService } from 'src/app/shared/services/nav-bar.service';
import { CssThemes } from 'src/app/shared/common';
import { ReactiveFormsModule } from '@angular/forms';
import { CustomDropdownComponent } from 'src/app/shared/component/custom-dropdown/custom-dropdown.component';
import { DropdownConfigurations, FilterDropDown, Icon, ImageIcon } from 'src/app/shared/interfaces';
import { LoadingService } from 'src/app/shared/helper';
import { ThemesService } from 'src/app/core/services/themes.service';
import { NotificationsButtonComponent } from '../notifications-button/notifications-button.component';
import { AuthenticationRepository } from 'src/app/core/data/repository';

@Component({
  standalone: true,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    MatMenuModule,
    MatIconModule,
    CommonModule,
    TranslateModule,
    CommonModule,
    IconSvgComponent,
    ReactiveFormsModule,
    CustomDropdownComponent,
    NotificationsButtonComponent
  ],
  providers: [HeaderBloc],
})
export class HeaderComponent extends BaseComponent implements OnInit {
  user!: AuthUser;
  form!: FormGroup;
  languageMenuItems: {language: LanguageEnum}[];
  //themes
  appTypes: FilterDropDown<string>[] = [];
  userProfileDropDownConfigs: DropdownConfigurations;
  userProfileIcon: Icon;
  currentUser: AuthUser;
  //getters
  get isSideNavCollapsed(): boolean {
    return this.navBarService.isSideNavCollapsed;
  }
  //constants
  IconsConstants = IconsConstants;

  // constructor
  constructor(
    private appBloc: AppBloc,
    private headerBloc: HeaderBloc,
    private navBarService: NavBarService,
    private authRepository: AuthenticationRepository,
    private themesService: ThemesService,
    injector: Injector
  ) {
    super(['ENUMS'], injector);
    this.currentUser = this.authRepository.currentUser$.value;
    this.listenToAppBlocState();
    this.listenToHeaderState();
    this.initializeForm();
    this.initializeLanguagesMenuItems();
    this.initializeProfileMenuItems();
  }

  // hooks
  ngOnInit(): void {
    this.getAuthenticatedUserData();
    this.userProfileIcon = this.setUserProfileIcon();
  }

  navigateToHomePage() {
    this.navigateToRoute('/');
  }

  // publics
  onClickMenuItem(action: ProfileMenuAction): void {
    switch (action) {
      case ProfileMenuAction.CHANGE_LANGUAGE: {
        this.checkCurrentLanguageAndChangeIt();
        break;
      }
      case ProfileMenuAction.LOGOUT: {
        this.headerBloc.setEvent.next(new HeaderLogOutEvent());
        break;
      }
    }
  }

  toggleSideNav(): void {
    this.injector.get(NavBarService).toggleSideNavBar();
  }

  onSelectionChange(selection: FilterDropDown<CssThemes>): void {
    this.injector.get(LoadingService).startLoading(SectionStateStatus.Loading)
    this.appBloc.setEvent.next(new UpdateAplicationTheme(selection.id));
    this.userProfileIcon = selection.dropDownValue?.['icon'];
  }

  // privates
  private initializeProfileMenuItems(): void {
    this.userProfileDropDownConfigs = {
      isMultipleSelect: false,
      placeholder: 'PROFILE',
      treeFormControlName: 'profile',
      panelClass: 'apps-dropdown'
    };
    this.appTypes =  this.currentUser.appsType?.length === 0 ? 
    this.getAllApplications() : this.getCurrentUserApps(this.currentUser)
  }

  private setUserProfileIcon(): ImageIcon {
   return this.appTypes.find(item => item.id == this.form.get('profile').value)?.dropDownValue?.['icon'];
  }

  private initializeLanguagesMenuItems(): void {
    this.languageMenuItems = [
      {
        language: LanguageEnum.Arabic
      },
      {
        language: LanguageEnum.English
      }
    ]
  }

  private onHandleAppBLocState(state: AppBlocState): void {
    if (state instanceof AuthenticatedUserDataReadyState) {
      this.user = state.user;
    }
  }

  private onHandleHeaderState(state: HeaderState): void {
    super.onHandleBlocState(state);
    if (state instanceof HeaderLoggedOutState) {
      this.navigateToRoute('/' + RoutesConstants.LOGIN_PAGE_URL);
    }
  }

  private getAuthenticatedUserData(): void {
    this.appBloc.setEvent.next(new InitEvent());
  }

  private listenToAppBlocState(): void {
    this.appBloc.onState.pipe(takeUntil(this.destroy$)).subscribe((state) => this.onHandleAppBLocState(state));
  }

  private listenToHeaderState(): void {
    this.headerBloc.onState.pipe(takeUntil(this.destroy$)).subscribe((state) => this.onHandleHeaderState(state));
  }

  private initializeForm(): void {
    this.form = new FormGroup({
      search: new FormControl(),
      profile: new FormControl(this.themesService.currentTheme)
    });
  }

  private getAllApplications(): FilterDropDown<string>[] {
    return [
      {
        id: CssThemes.SUPER_ADMIN,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['SUPER_ADMIN'],
        dropDownValue: {
          id: CssThemes.SUPER_ADMIN,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['MODDAKIR'],
          icon: IconsMap.get(CssThemes.SUPER_ADMIN)
        }
      },
      {
        id: CssThemes.MODDAKIR,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['MODDAKIR'],
        dropDownValue: {
          id: CssThemes.MODDAKIR,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['MODDAKIR'],
          icon: IconsMap.get(CssThemes.MODDAKIR)
        }
      },
      {
        id: CssThemes.MODDAKIR_KIDS,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['MODDAKIR_KIDS'],
        dropDownValue: {
          id: CssThemes.MODDAKIR_KIDS,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['MODDAKIR_KIDS'],
          icon: IconsMap.get(CssThemes.MODDAKIR_KIDS)
        }
      },
      {
        id: CssThemes.WL2,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_2'],
        dropDownValue: {
          id: CssThemes.WL2,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_2'],
          icon: IconsMap.get(CssThemes.WL2)
        }
      },
      {
        id: CssThemes.WL3,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_3'],
        dropDownValue: {
          id: CssThemes.WL3,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_3'],
          icon: IconsMap.get(CssThemes.WL3)
        }
      },
      {
        id: CssThemes.BREDA,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['BREDA'],
        dropDownValue: {
          id: CssThemes.BREDA,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['BREDA'],
          icon: IconsMap.get(CssThemes.BREDA)
        }
      },
      {
        id: CssThemes.WL5,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_5'],
        dropDownValue: {
          id: CssThemes.WL5,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_5'],
          icon: IconsMap.get(CssThemes.WL5)
        }
      },
      {
        id: CssThemes.WL6,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_6'],
        dropDownValue: {
          id: CssThemes.WL6,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION']['WL_6'],
          icon: IconsMap.get(CssThemes.WL6)
        }
      },
    ]
  }

  private getCurrentUserApps(currentUser: AuthUser): FilterDropDown<string>[] {
    return currentUser.appsType
    ?.map(appType => ({
        id: appType,
        dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION'][appType?.toUpperCase()],
        dropDownValue: {
          id: appType,
          dropDownDisplayName: this.translationsList['ENUMS']['APPLICATION'][appType?.toUpperCase()],
          icon: IconsMap.get(appType)
        }
      }))
    .sort((a, b) => {
      if (a.id === ApplicationType.MODDAKIR) return -1; // Move 'moddakir' to the beginning
      if (b.id === ApplicationType.MODDAKIR) return 1;  // Move 'moddakir' to the beginning
      return 0;
    });
  }
}

const IconsMap: Map<string, ImageIcon> = new Map<string, ImageIcon>([
  [CssThemes.MODDAKIR, IconsConstants.MODDAKIR_ICON],
  [CssThemes.WL2, IconsConstants.TAALAM_ICON],
  [CssThemes.WL3, IconsConstants.RATTEL_ICON],
  [CssThemes.WL5, IconsConstants.TEGAN_ICON],
  [CssThemes.BREDA, IconsConstants.BREDA_ICON],
  [CssThemes.SUPER_ADMIN, IconsConstants.SUPER_ADMIN_ICON],
  [CssThemes.MODDAKIR_KIDS, IconsConstants.MODDAKIR_KIDS_ICON],
  [CssThemes.WL6, IconsConstants.TEBYAN_ICON],
]);